'use strict';

define('vb/private/history',[
  'vb/private/utils',
  'vb/private/constants',
  'vb/binding/expression',
], (Utils, Constants, Expression) => {
  const VB_STATE_PROPERTY = 'vbState';
  const REPLACE_STATE_OP = 'replaceState';
  const PUSH_STATE_OP = 'pushState';
  let paramsToIgnore;
  /**
   * Cached value of the browser state.
   * Initialized in reset()
   * @type {Object}
   */
  let cachedState;

  /**
   * @type {URL}
   */
  let url;

  /**
   * @type {String}
   */
  let stateOp;

  /**
   * @type {Object}
   */
  let cleanUrlParameters;

  /**
   * @type {Object}
   */
  let navBackParams;

  /**
   * Used to cache the history state before a call to the history popState event
   * @type {Object<Object, String>|null}
   */
  let stateBeforeHistoryPop = null;

  /**
   * @type {boolean}
   */
  let skipMode;

  /**
   * Stack of history state
   * @type {Array<Object>}
   */
  let stack = [];

  /**
   * @type {number}
   */
  let index = 0;

  /**
   * @type {number}
   */
  let direction = Constants.NavigateDirection.FORWARD;

  /**
   * A number representing how much move in history stack
   * @type {number}
   */
  let change = 0;

  /**
   *  A flag used when history.back() is called from this class to restore the URL and history stack
   *  @type {boolean}
   */
  let ignoreNextPopStateEvent = false;

  /**
   * A flag set when an anchor tag with href="#" was detected
   * @type {boolean}
   */
  let foundHrefWithHash = false;

  /**
   * Function to build a key to store the value of a container variable on the browser history state.
   * The scope name is needed to differentiate variable of the same name in different container
   *
   * @param      {String}  scopeName  The scope name
   * @param      {String}  namespace  The namespace (variables, constants, ...)
   * @param      {String}  name       The name
   * @return     {String}  The variable key.
   */
  const buildVariableKey = (scopeName, namespace, name) => `${scopeName}.${namespace}.${name}`;

  /**
   * Remove brackets ([] or {}) wrapping a request parameter so that it cannot be interpreted
   * as an expression
   *
   * @param  {String} urlParam - The url parameter
   * @return {String}
   */
  const cleanUrlParameter = (urlParam) => {
    let cleanUrlParam;
    if (typeof urlParam === 'string') {
      const expression = Expression.getExpression(urlParam);
      if (expression) {
        // Retrieve the string inside the expression and recurse in case brackets are nested
        // Expression.getExpression(urlParam) cannot be null or undefined because we know it
        // is an expression.
        cleanUrlParam = cleanUrlParameter(expression.trim());
      } else {
        cleanUrlParam = urlParam;
      }
    } else if (urlParam === null) {
      cleanUrlParam = null;
    }

    return cleanUrlParam;
  };

  /**
   * Add a parameter to the key/value map of URL parameters urlParams.
   * Support arrays when the parameter names shows more than once in the URL
   * @param {Object} urlParams
   * @param {String} key
   * @param {*} value
   */
  const calculateParamValue = (urlParams, key, value) => {
    const cleanValue = cleanUrlParameter(value);
    const existingValue = urlParams[key];
    // If a param of the same name exists, make an array for the value
    if (existingValue) {
      if (Array.isArray(existingValue)) {
        existingValue.push(cleanValue);
      } else {
        // eslint-disable-next-line no-param-reassign
        urlParams[key] = [existingValue, cleanValue];
      }
    } else {
      // eslint-disable-next-line no-param-reassign
      urlParams[key] = cleanValue;
    }
  };

  /**
   * Replace the state of the current element in the history stack with the current cachedState.
   */
  const stackReplace = () => {
    stack[index] = Object.assign({}, cachedState);
  };

  /**
   * Push a new entry after the current element in the history stack and save to current cachedState to it.
   * All elements in the history after the current element are removed.
   */
  const stackPush = () => {
    // Remove all elements in the stack after the current one and add the new one to the end
    stack.splice(index + 1, stack.length - 1 - index, Object.assign({}, cachedState));
    index = stack.length - 1;
  };

  /**
   * The History class has 3 purposes:
   *   1) abstract the window.history API
   *   2) defer any updates to the browser history until sync is called to minimized the amount
   *      of change to the browser history
   *   3) get around Google chrome throttling of the history state
   *
   * The state for VB is stored in a sub-property named 'vbState' to not conflict with other
   * state also stored in the browser history.
   */
  class History {
    /**
     * initialize the History class.
     *   - cached state is empty
     *   - any URL params are copied internally if clearParameter is truthy
     *   - URL params are removed from the search
     *   - operation is set to replaceState
     * @param  {function(Object):void} popStateCallback register a callback on the history popstate event
     */
    static init(popStateCallback = () => {}) {
      // Add a listener to the popstate event so when navigating using the browser back button
      // or when history.back() is called we know to update the cached state
      window.onpopstate = (event) => {
        // When history.back() id called to cancel the browser forward button, the ignoreNextPopStateEvent is
        // set to true so that we ignore the popState event
        if (ignoreNextPopStateEvent) {
          ignoreNextPopStateEvent = false;
          return;
        }

        if (foundHrefWithHash) {
          ignoreNextPopStateEvent = true;
          foundHrefWithHash = false;
          // Restore the history to before it was pushed by the href="#"
          window.history.back();
          return;
        }

        History.popStateEventHandler(event);
        popStateCallback({ direction, change });
      };

      History.reset(false);
    }

    /**
     * Resets the given clear parameters.
     * Called internally and by unit tests.
     *
     * @param {boolean} [clearParameters=false] if true, URL params are not stored internally
     */
    static reset(clearParameters = false) {
      cachedState = { id: Utils.generateUniqueId() };
      stackPush();

      url = new URL(window.location.href);

      const urlParams = {};
      paramsToIgnore = {};

      // Make a copy of the original query param and clean up the URL
      // This is so query param that are not part matching any fromUrl variable are removed
      // from the URL when that application is started
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of url.searchParams) {
        if (Constants.UrlParamsToIgnore.includes(key)) {
          // Keep track of any parameters to ignore, this will be used when resetting the URL
          paramsToIgnore[key] = value;
        } else {
          calculateParamValue(urlParams, key, value);
        }
      }

      stateOp = REPLACE_STATE_OP;
      skipMode = false;

      // erase all query params then restore the param to ignore
      History.resetUrlParameters();

      if (!clearParameters) {
        cleanUrlParameters = urlParams;
      }
    }

    /**
     * A listener for the popstate event so when navigating using the browser back button.
     *
     * @param {{ state: Object|null }} event the popState event
     */
    static popStateEventHandler({ state }) {
      // Make a copy of the state before changing it. It will be used to restore state and url
      // if the navigation is cancelled.
      stateBeforeHistoryPop = {
        state: History.state,
        url: url.href,
      };

      // It is possible for event.state to be null, for example if a previous pushState used
      // null for the state argument or when a link with href="#" is clicked
      if (state === null) {
        const oldUrl = new URL(url.href);
        oldUrl.hash = '';
        const newUrl = new URL(window.location.href);
        newUrl.hash = '';
        const samePage = newUrl.href === oldUrl.href;

        // Do not loose internal state if navigating to the same page
        if (!samePage) {
          History.replaceState(null);
        }
        index = 0;
        stackPush();
        direction = Constants.NavigateDirection.FORWARD;
      } else {
        const vbState = state[VB_STATE_PROPERTY];
        const newIndex = stack.findIndex(({ id }) => id === vbState.id);
        if (newIndex >= 0) {
          change = newIndex - index;
          if (newIndex - index > 0) {
            direction = Constants.NavigateDirection.FORWARD;
          } else {
            direction = Constants.NavigateDirection.BACKWARD;
            change = -change;
          }
          index = newIndex;
        } else {
          direction = Constants.NavigateDirection.FORWARD;
          change = 0;
          stack = [vbState];
          index = 0;
        }
        History.replaceState(vbState);
      }

      // Merge navBackParams on top of the cachedState inputParameters
      // This is to properly handle input parameters coming from the navigateBack action
      if (navBackParams) {
        if (Utils.isObject(cachedState.inputParameters)) {
          Utils.cloneObject(navBackParams, cachedState.inputParameters);
        } else {
          cachedState.inputParameters = Utils.cloneObject(navBackParams);
        }

        navBackParams = undefined;
      }

      // Create a new URL object using the existing browser URL
      url = new URL(window.location.href);
      // Repopulate the url params that will be used by manageInputParameters
      cleanUrlParameters = History.retrieveUrlParameters();
      stateOp = REPLACE_STATE_OP;
    }

    /**
     * @param {boolean} value
     */
    static foundHrefWithHash(value) {
      foundHrefWithHash = value;
    }

    /**
     * Used by vbRouter to set the new URL
     *
     * @param {String} newUrl - The new value
     */
    static setUrl(newUrl) {
      if (!skipMode) {
        const { search } = url;
        url = new URL(newUrl);
        url.search = search;
      }
    }

    /**
     * Reset the pathname part of the current url.
     * This is needed after the router navigated event to update the url
     * with the new path set by the router. Input parameter need to be
     * kept around to initialize the fromUrl variable.
     */
    static resetUri() {
      if (!skipMode) {
        const { search, hash } = url;
        url = new URL(window.location.href);
        url.search = search;
        url.hash = hash;
      }
    }

    /**
     * Parse the URL parameters and return an object which properties are parameter names
     * making sure values of URL parameters are not expressions.
     *
     * @return {Object}  The url parameters.
     */
    static retrieveUrlParameters() {
      const urlParams = {};

      url.searchParams.forEach((value, key) => {
        if (!Object.keys(paramsToIgnore).includes(key)) {
          calculateParamValue(urlParams, key, value);
        }
      });

      return urlParams;
    }

    /**
     * Reset the URL parameters except for the one to ignore
     * This is used before navigating to clean up for the new state.
     */
    static resetUrlParameters() {
      if (!skipMode) {
        url.search = '';
        // Restore the param to ignore
        Object.keys(paramsToIgnore).forEach((param) => {
          url.searchParams.set(param, paramsToIgnore[param]);
        });
        cleanUrlParameters = {};
      }
    }

    /**
     * @param {boolean} flag
     */
    static setSkipMode(flag) {
      skipMode = flag;
    }

    /**
     * @returns {boolean}
     */
    static getSkipMode() {
      return skipMode;
    }

    /**
     * Retrieve the current cached state. Never returns undefined or null.
     *
     * @return {Object} the current state
     */
    static get state() {
      return Utils.cloneObject(cachedState);
    }

    /**
     * Set the browser state operation to be pushState
     */
    static pushState() {
      stateOp = PUSH_STATE_OP;
    }

    /**
     * Replace the currently cached browser state and URL.
     * @param  {Object} state the new state
     */
    static replaceState(state) {
      cachedState = state ? Utils.cloneObject(state) : {
        id: Utils.generateUniqueId(),
      };
    }

    /**
     * Add query parameters coming from an array of object representing
     * the routers state.
     *
     * @param {Array.<{name: string, id: string}>} params
     */
    static addRouterQueryParameters(params) {
      if (!skipMode) {
        params.forEach((param) => {
          if (param.name && param.id !== undefined) {
            url.searchParams.set(param.name, param.id);
          }
        });

        // searchParams API replaces space character with '+' but some VB app
        // expect %20 which is a valid alternative
        url.search = url.search.replaceAll('+', '%20');
      }
    }

    /**
     * Set or delete a URL parameter on a URL object
     * This routine is used by setUrlParameter and the scope object for variable of type fromUrl
     * @param {URL} urlObj
     * @param {String} name
     * @param {*} value
     */
    static changeUrlParameter(urlObj, name, value) {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          // Remove existing entry
          urlObj.searchParams.delete(name);
          const cleanArray = [];
          // and replace with a new one
          value.forEach((paramValue) => {
            const cleanValue = cleanUrlParameter(String(paramValue));
            urlObj.searchParams.append(name, cleanValue);
            cleanArray.push(cleanValue);
          });
          cleanUrlParameters[name] = cleanArray;
        } else {
          const cleanValue = cleanUrlParameter(String(value));
          urlObj.searchParams.set(name, cleanValue);
          cleanUrlParameters[name] = cleanValue;
        }
      } else {
        urlObj.searchParams.delete(name);
        delete cleanUrlParameters[name];
      }

      // searchParams API replaces space character with '+' but some VB app
      // expect %20 which is a valid alternative
      // Need to be done even when deleting a param from the URL because
      // URL API replace search '%20' with '+'
      // eslint-disable-next-line no-param-reassign
      urlObj.search = urlObj.search.replaceAll('+', '%20');
    }

    /**
     * Sets the url parameter. If the value is undefined, the param is
     * removed from the URL
     *
     * @param {String} name
     * @param {*} value
     */
    static setUrlParameter(name, value) {
      if (!skipMode) {
        History.changeUrlParameter(url, name, value);
      }
    }

    /**
     * Gets the url parameter clean value (no expression backet)
     *
     * @param  {String} name
     * @return {String} clean parameter value
     */
    static getUrlParameter(name) {
      return cleanUrlParameters[name];
    }

    /**
     * Retrieve the search section of the current URL
     * @return {String}
     */
    static getSearch() {
      return url.search;
    }

    /**
     * Replace the search section of the current URL
     * @param {String} search
     */
    static setSearch(search = '') {
      if (!skipMode) {
        url.search = search;
      }
    }

    /**
     * Retrieve the hash section of the cached URL
     * @return {String} hash
     */
    static getHash() {
      return url.hash;
    }

    /**
     * Replace the hash section of the cached URL
     * @param {String} hash
     */
    static setHash(hash = '') {
      url.hash = hash;
    }

    /**
     * Store a copy of the input parameters in history.
     * It is the options.params argument of the navigateToPage action. This is needed
     * in two situation:
     *   1) When navigating back to this page and initializing the page input parameter.
     *   2) When navigating to the same page to compare if the input parameters are different.
     *
     * @param {Object} inputParameters an object where the property keys are params name and
     * the property values the params values.
     */
    static setInputParameters(inputParameters) {
      cachedState.inputParameters = Utils.cloneObject(inputParameters);
    }

    static getInputParameters() {
      return cachedState.inputParameters;
    }

    /**
     * @param {string} pagePath
     */
    static setPagePath(pagePath) {
      cachedState.pagePath = pagePath;
    }

    /**
     * @returns {string}
     */
    static getPagePath() {
      return cachedState.pagePath;
    }

    /**
     * Used to store history persisted variables.
     * Variable state is stored in state.variables[${namespace}.${name}]
     *
     * @param {String} scopeName
     * @param {String} namespace
     * @param {String} name
     * @param {String} value - The value of the variable in a serializable form (string)
     */
    static setVariable(scopeName, namespace, name, value) {
      const key = buildVariableKey(scopeName, namespace, name);

      if (value !== undefined) {
        if (!cachedState.variables) {
          cachedState.variables = {};
        }
        cachedState.variables[key] = value;
      } else if (cachedState.variables) {
        delete cachedState.variables[key];
      }
    }

    /**
     * Used to retrieve history persisted variables.
     * Return undefined, if the variable value is not found
     *
     * @param  {String} scopeName
     * @param  {String} namespace
     * @param  {String} name
     * @return {String} the value of the variable in a serializable form (string)
     */
    static getVariable(scopeName, namespace, name) {
      return cachedState.variables && cachedState.variables[buildVariableKey(scopeName, namespace, name)];
    }

    static getVariables() {
      return cachedState.variables || {};
    }

    /**
     * Reset page history persisted variable from the cache
     * This is a local function but need to be called from tests
     */
    static resetPageVariables() {
      // VBS-34264 - Temporarily comment out this, reverting part of VBS-30648
      // Object.keys(History.getVariables()).forEach((key) => {
      //   const scopeName = key.substring(0, key.indexOf('.'));
      //   const className = scopeName.substring(0, scopeName.indexOf('/'));
      //   // Matches "Page" and "PackagePage"
      //   if (className.indexOf('Page') >= 0) {
      //     delete cachedState.variables[key];
      //   }
      // });
    }

    /**
     * Stores a value in the history state using a string key
     *
     * @param {String}  key     The key
     * @param {Object}  value   The value
     */
    static storeInState(key, value) {
      if (value !== undefined) {
        cachedState.storage = cachedState.storage || {};
        cachedState.storage[key] = value;
      } else if (cachedState.storage) {
        delete cachedState.storage[key];
      }
    }

    /**
     * Retrieves a value from the history state using a string key
     *
     * @param  {String}  key     The key
     * @return {Object}  The value
     */
    static retrieveFromState(key) {
      return cachedState.storage && cachedState.storage[key];
    }

    /**
     * The way the navigateBack action goes back in history when not in a switcher.
     * Only called by the navigateBack action.
     * @param {Object} [params] the input parameters on the page it is
     * navigating back to.
     */
    static backAction(params) {
      navBackParams = params;
      window.history.back();
    }

    /**
     * Apply cached changes to the browser history or URL.
     * @return {Promise} a promise that resolves when the browser history changed
     */
    static sync() {
      const { href } = url;

      stateBeforeHistoryPop = null;

      if (stateOp === REPLACE_STATE_OP) {
        let historyState = window.history.state;
        const vbState = historyState && historyState[VB_STATE_PROPERTY];

        if (href !== window.location.href || Utils.diff(cachedState, vbState)) {
          // An empty cacheState is the same as undefined
          if (Object.keys(cachedState).length > 0) {
            if (!historyState) {
              historyState = {};
            }
            // Our state is always store in the property 'vbState' of the history state
            historyState[VB_STATE_PROPERTY] = cachedState;
          } else if (historyState) {
            delete historyState[VB_STATE_PROPERTY];
          }

          return Utils.changeBrowserState(historyState, href, stateOp)
            .then(() => {
              stackReplace();
            });
        }
        return Promise.resolve();
      }
      // In the pushState case, don't need to merge with existing state
      cachedState.id = Utils.generateUniqueId();
      // Instead of getting a new state and copying the history persisted variables for flow, app, and page
      // when navigating to the same page, current state is kept and the page persisted variables are removed.
      // This is so we will not find them when navigating to this page later
      if (href !== window.location.href) {
        History.resetPageVariables();
      }
      return Utils.changeBrowserState({ [VB_STATE_PROPERTY]: cachedState }, href, stateOp).then(() => {
        stackPush();
        stateOp = REPLACE_STATE_OP;
      });
    }

    /**
     * Returns the state before the last popState event
     * @returns {Object<Object, String>}
     */
    static getStateBeforeHistoryPop() {
      return stateBeforeHistoryPop || {};
    }

    /**
     * Restore the URL and the history state to the value before the browser back/forward button.
     * This is to be used when a navigation initiated by a browser back/forward button is cancelled.
     * @return {Promise} a promise that resolve when the browser history is restored
     */
    static restoreStateBeforeHistoryPop(eventPayload) {
      if (stateBeforeHistoryPop
        // Only restore the state if the the navigation was initiated from a browser back or forward button
        // with only one step change on the stack
        && eventPayload && eventPayload.origin === Constants.NavigateOrigin.POPSTATE
        && eventPayload.canBeCanceled) {
        if (eventPayload.direction === Constants.NavigateDirection.BACKWARD) {
          // On back button the browser always pop the history stack, so to restore the stack on cancel,
          // the state is pushed on the stack.
          cachedState = stateBeforeHistoryPop.state;
          url = new URL(stateBeforeHistoryPop.url);

          return Utils.changeBrowserState({ [VB_STATE_PROPERTY]: cachedState },
            stateBeforeHistoryPop.url, PUSH_STATE_OP)
            .then(() => {
              index += eventPayload.steps;
              stateOp = REPLACE_STATE_OP;
            });
        }

        if (eventPayload.direction === Constants.NavigateDirection.FORWARD) {
          // On forward button the browser always move the history stack forward, so to restore the stack on cancel,
          // calls history.back.
          // Use the ignoreNextPopStateEvent marker to skip the normal popstate processing in that case
          ignoreNextPopStateEvent = true;
          index -= eventPayload.steps;
          window.history.back();
        }
      }

      return Promise.resolve();
    }

    /**
     * Return the browser history state with the current VB cachedState
     * This is used when it's needed to replace the browser URL with 'replaceState'
     * with the correct state.
     * @return {Object} browser history state object
     */
    static getState() {
      const { state } = window.history;
      const newState = state || {};

      newState[VB_STATE_PROPERTY] = cachedState;

      return newState;
    }

    /**
     * Traverse the browser history stack to find the entry matching the given page path.
     * If found, returns the path of the previous entry un the history stack.
     * Returns null if path is not found in the stack
     * Returns an empty string if there is no history before that element
     *
     * @param {string} path - The path of the page to find the browser history
     * @returns {string|null} The path of the page of the previous stack entry
     */
    static getPathBeforePreviousVisit(path) {
      // Search backward from the current history position for the previous entry matching the path,
      // ignoring the current state itself and any forward entries after the current index.
      let location = -1;
      for (let i = index; i >= 0; i -= 1) {
        const elt = stack[i];
        if (elt.pagePath === path && elt.id !== cachedState.id) {
          location = i;
          break;
        }
      }
      if (location === -1) {
        return null;
      }

      // If the element is found at the beginning of the stack, there is nowhere to go back to.
      if (location === 0) {
        return '';
      }

      // then return path of the location one back in the history
      return stack[location - 1].pagePath;
    }
  // end of history class
  }

  return History;
});

